import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  MobileStepper,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ZoomIn,
  ZoomOut,
  FitScreen,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { StaffService } from "../../../../services/staffandplant/staffService";

const ImagePreviewModal = ({
  open,
  onClose,
  fileId,
  fileName,
  isOrg = false,
  // Navigation props
  documents = [],
  currentIndex = 0,
  onNavigate,
  title,
}) => {
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageScale, setImageScale] = useState(1);
  const [imageLoading, setImageLoading] = useState(false);

  const hasMultipleDocs = documents.length > 1;
  const currentDoc = documents[currentIndex] || { id: fileId, name: fileName };

  useEffect(() => {
    if (open && fileId) {
      fetchFileUrl();
    }
     // eslint-disable-next-line
  }, [open, fileId]);

  const fetchFileUrl = async () => {
    if (!fileId) return;

    setLoading(true);
    setError(null);

    try {
      const url = await StaffService.getFileUrl(fileId, true);
      setFileUrl(url);
    } catch (err) {
      console.error("Error fetching file URL:", err);
      setError("Failed to load file");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!fileId) {
      console.error("No file ID available for download");
      return;
    }

    try {
      const getContentType = (filename) => {
        const ext = filename.toLowerCase().split(".").pop();
        const contentTypes = {
          pdf: "application/pdf",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          gif: "image/gif",
          webp: "image/webp",
          bmp: "image/bmp",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          xls: "application/vnd.ms-excel",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          txt: "text/plain",
          zip: "application/zip",
          rar: "application/x-rar-compressed",
        };
        return contentTypes[ext] || "application/octet-stream";
      };

      const docName = currentDoc.name || fileName || "document";
      const fileExtension = getFileExtension(fileUrl);
      let downloadFileName = docName;
      if (!downloadFileName.includes(".") && fileExtension) {
        downloadFileName += fileExtension;
      }
      downloadFileName = downloadFileName.replace(/[<>:"/\\|?*]/g, "_");

      const downloadUrl = await StaffService.getFileUrl(fileId, isOrg);

      if (
        !downloadUrl ||
        typeof downloadUrl !== "string" ||
        !downloadUrl.startsWith("http")
      ) {
        throw new Error("Invalid download URL received from server");
      }

      const fileResponse = await fetch(downloadUrl);

      if (!fileResponse.ok) {
        throw new Error(
          `Failed to fetch file: ${fileResponse.status} ${fileResponse.statusText}`
        );
      }

      const contentType =
        fileResponse.headers.get("content-type") ||
        getContentType(downloadFileName);

      const arrayBuffer = await fileResponse.arrayBuffer();

      const blob = new Blob([arrayBuffer], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadFileName);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);

      if (fileUrl) {
        try {
          const response = await fetch(fileUrl);

          if (!response.ok) {
            throw new Error(`Fallback fetch failed: ${response.status}`);
          }

          const docName = currentDoc.name || fileName || "document";
          const fileExtension = getFileExtension(fileUrl);
          let downloadFileName = docName;
          if (!downloadFileName.includes(".") && fileExtension) {
            downloadFileName += fileExtension;
          }
          downloadFileName = downloadFileName.replace(/[<>:"/\\|?*]/g, "_");

          const getContentType = (filename) => {
            const ext = filename.toLowerCase().split(".").pop();
            const contentTypes = {
              pdf: "application/pdf",
              jpg: "image/jpeg",
              jpeg: "image/jpeg",
              png: "image/png",
              gif: "image/gif",
              webp: "image/webp",
              bmp: "image/bmp",
              doc: "application/msword",
              docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              xls: "application/vnd.ms-excel",
              xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              txt: "text/plain",
              zip: "application/zip",
              rar: "application/x-rar-compressed",
            };
            return contentTypes[ext] || "application/octet-stream";
          };

          const contentType =
            response.headers.get("content-type") ||
            getContentType(downloadFileName);
          const arrayBuffer = await response.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: contentType });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", downloadFileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (fallbackError) {
          console.error("Fallback download also failed:", fallbackError);

          try {
            const docName = currentDoc.name || fileName || "document";
            const link = document.createElement("a");
            link.href = fileUrl;
            link.setAttribute("download", docName);
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } catch (lastResortError) {
            console.error("All download methods failed:", lastResortError);
          }
        }
      }
    }
  };
  const getFileExtension = (url) => {
    if (!url) return "";

    const urlParts = url.split(".");
    if (urlParts.length > 1) {
      const ext = urlParts[urlParts.length - 1].split("?")[0].split("#")[0];
      if (ext && ext.length <= 5) {
        return `.${ext.toLowerCase()}`;
      }
    }

    if (isImageFile(url)) {
      if (url.includes("jpeg") || url.includes("jpg")) return ".jpg";
      if (url.includes("png")) return ".png";
      if (url.includes("gif")) return ".gif";
      if (url.includes("webp")) return ".webp";
      if (url.includes("svg")) return ".svg";
      return ".jpg";
    }

    if (url.includes("pdf")) return ".pdf";
    return "";
  };

  const isImageFile = (url) => {
    if (!url) return false;
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
    ];
    const urlLower = url.toLowerCase();
    return (
      imageExtensions.some((ext) => urlLower.includes(ext)) ||
      url.includes("image") ||
      url.includes("photo")
    );
  };

  const handleNavigation = (direction) => {
    if (!hasMultipleDocs || !onNavigate) return;

    onNavigate(direction);
    setImageScale(1);

    const newDoc =
      documents[
        direction === "next"
          ? (currentIndex + 1) % documents.length
          : (currentIndex - 1 + documents.length) % documents.length
      ];

    if (newDoc.upload_id || newDoc.id) {
      fetchFileUrl();
    }
  };

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev - 0.25, 0.25));
  };

  const handleFitToScreen = () => {
    setImageScale(1);
  };

  const handleClose = () => {
    setFileUrl("");
    setError(null);
    setImageScale(1);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: { xs: "95vh", sm: "90vh" },
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" noWrap>
              {title || fileName || "Document Preview"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Zoom Controls for Images */}
              {isImageFile(fileUrl) && (
                <>
                  <Tooltip title="Zoom Out">
                    <IconButton
                      onClick={handleZoomOut}
                      disabled={imageScale <= 0.25}
                      size="small"
                    >
                      <ZoomOut />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{ minWidth: "40px", textAlign: "center" }}
                  >
                    {Math.round(imageScale * 100)}%
                  </Typography>
                  <Tooltip title="Zoom In">
                    <IconButton
                      onClick={handleZoomIn}
                      disabled={imageScale >= 3}
                      size="small"
                    >
                      <ZoomIn />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Fit to Screen">
                    <IconButton onClick={handleFitToScreen} size="small">
                      <FitScreen />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Download">
                <IconButton
                  onClick={handleDownload}
                  disabled={!fileUrl}
                  size="small"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="subtitle2" noWrap sx={{ width: "100%" }}>
            {currentDoc.name || fileName || ""}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: 0,
          overflow: "hidden",
          height: "100%",
          minHeight: 0,
          bgcolor: "#f5f5f5",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Typography color="error" variant="h6">
              {error}
            </Typography>
            <Button onClick={fetchFileUrl} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        ) : fileUrl ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                width: "100%",
                height: "100%",
                minHeight: 0,
                overflow: isImageFile(fileUrl) ? "auto" : "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
              }}
            >
              {isImageFile(fileUrl) ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  {imageLoading && (
                    <CircularProgress
                      sx={{
                        position: "absolute",
                        color: "#1976D2",
                      }}
                    />
                  )}
                  <img
                    src={fileUrl}
                    alt={fileName}
                    onLoad={() => setImageLoading(false)}
                    onLoadStart={() => setImageLoading(true)}
                    style={{
                      maxWidth: imageScale === 1 ? "100%" : "none",
                      maxHeight: imageScale === 1 ? "100%" : "none",
                      width: imageScale !== 1 ? `${imageScale * 100}%` : "auto",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      backgroundColor: "white",
                      display: imageLoading ? "none" : "block",
                    }}
                  />
                </Box>
              ) : (
                <iframe
                  src={fileUrl}
                  title={fileName}
                  style={{
                    border: "none",
                    width: "100%",
                    height: "100%",
                    minHeight: 0,
                    display: "block",
                    borderRadius: "8px",
                    backgroundColor: "white",
                  }}
                />
              )}
            </Box>

            {/* Navigation Controls */}
            {hasMultipleDocs && (
              <MobileStepper
                variant="text"
                steps={documents.length}
                position="static"
                activeStep={currentIndex}
                nextButton={
                  <Button
                    size="small"
                    onClick={() => handleNavigation("next")}
                    sx={{
                      color: "#1976D2",
                      "&:hover": { backgroundColor: "#f0f7ff" },
                    }}
                  >
                    Next
                    <KeyboardArrowRight />
                  </Button>
                }
                backButton={
                  <Button
                    size="small"
                    onClick={() => handleNavigation("prev")}
                    sx={{
                      color: "#1976D2",
                      "&:hover": { backgroundColor: "#f0f7ff" },
                    }}
                  >
                    <KeyboardArrowLeft />
                    Back
                  </Button>
                }
                sx={{ bgcolor: "#fff" }}
              />
            )}
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No document to preview
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Close
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {hasMultipleDocs && (
            <Typography variant="caption" color="text.secondary">
              {currentIndex + 1} of {documents.length} documents
            </Typography>
          )}
          <Button
            onClick={handleDownload}
            disabled={!fileUrl}
            variant="contained"
            sx={{
              backgroundColor: "#1976D2",
              "&:hover": { backgroundColor: "#1565C0" },
              textTransform: "none",
            }}
          >
            Download
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ImagePreviewModal;
